import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMeetingLock } from '../contexts/MeetingLockContext';
import { toast } from 'sonner';

/**
 * Inactivity auto-logout.  Logs the user out after 2.5 hours of NO user
 * activity (no mouse, keyboard, scroll, or touch events).  The timer is
 * paused while a meeting is in progress because the user is typically
 * not interacting with the app during a meeting and we explicitly want
 * the app to stay open until the After selfie is taken.
 *
 * A 2-minute warning toast is shown before the auto-logout fires.
 */
const TIMEOUT_MS = 2.5 * 60 * 60 * 1000;       // 2.5 hours
const WARNING_MS = TIMEOUT_MS - 2 * 60 * 1000; // 2 minutes before logout

export default function InactivityTimeout() {
  const { user, logout } = useAuth();
  const { active: meetingInProgress } = useMeetingLock();
  const lastActivityRef = useRef(Date.now());
  const warnedRef = useRef(false);
  const tickIdRef = useRef(null);

  useEffect(() => {
    if (!user) return undefined;

    const reset = () => {
      lastActivityRef.current = Date.now();
      warnedRef.current = false;
    };

    const events = ['mousemove', 'mousedown', 'touchstart', 'keydown', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));

    tickIdRef.current = setInterval(() => {
      // Pause the timer entirely while a meeting is in progress.  This is
      // explicit user-requested behaviour:  the app must stay open during
      // the meeting (between Before and After selfies).
      if (meetingInProgress) { reset(); return; }

      const idle = Date.now() - lastActivityRef.current;
      if (idle >= TIMEOUT_MS) {
        clearInterval(tickIdRef.current);
        tickIdRef.current = null;
        toast.error('You have been inactive for 2.5 hours.  Logging out for your security.');
        // brief defer so the toast paints before the redirect.
        setTimeout(() => logout(), 800);
      } else if (idle >= WARNING_MS && !warnedRef.current) {
        warnedRef.current = true;
        toast.warning('You will be logged out in 2 minutes due to inactivity.  Move the page to stay logged in.');
      }
    }, 30_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (tickIdRef.current) clearInterval(tickIdRef.current);
    };
  }, [user, meetingInProgress, logout]);

  return null;
}
